import { Fragment, ReactNode, useMemo } from 'react';

import { Dialog, Transition } from '@headlessui/react';
import { twMerge } from 'tailwind-merge';

const DialogContainer = ({
  children,
  isOpen,
  handleClose,
  className,
  zIndex = 50,
  wrapperClassName = 'p-4',
  disableMaxWidth,
  afterLeave,
  animation = 'scale',
  unmount,
}: {
  wrapperClassName?: string;
  className?: string;
  children: ReactNode;
  isOpen: boolean;
  handleClose: () => void;
  zIndex?: number;
  disableMaxWidth?: boolean;
  afterLeave?: () => void;
  animation?: 'slide-to-top' | 'scale';
  unmount?: boolean;
}) => {
  const selectedAnimation = useMemo(() => {
    switch (animation) {
      case 'slide-to-top':
        return {
          enter: 'transform transition-all duration-300',
          enterFrom: 'translate-y-full',
          enterTo: 'translate-y-0',
          leave: 'transform transition-all duration-200',
          leaveFrom: 'translate-y-0',
          leaveTo: 'translate-y-full',
        };
      case 'scale':
      default:
        return {
          enter: 'ease-out duration-300',
          enterFrom: 'opacity-0 scale-95',
          enterTo: 'opacity-100 scale-100',
          leave: 'ease-in duration-200',
          leaveFrom: 'opacity-100 scale-100',
          leaveTo: 'opacity-0 scale-95',
        };
    }
  }, [animation]);

  return (
    <Transition appear show={isOpen} as={Fragment} afterLeave={afterLeave} unmount={unmount}>
      <Dialog as="div" style={{ zIndex }} className="relative" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div style={{ zIndex }} className="fixed inset-0 bg-v2blueGray-800/30" />
        </Transition.Child>

        <div style={{ zIndex }} className="fixed inset-0 overflow-y-auto">
          <div
            className={twMerge(
              'flex min-h-full items-center justify-center text-center',
              wrapperClassName,
            )}
          >
            <Transition.Child as={Fragment} {...selectedAnimation}>
              <Dialog.Panel
                className={twMerge(
                  !disableMaxWidth && 'max-w-md',
                  'w-full transform rounded-[30px] bg-white p-8 text-left align-middle shadow-xl transition-all',
                  className,
                )}
              >
                {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export { DialogContainer };
